import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Typography,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { 
  fetchOrderDetails, 
  fetchPayments,
} from "../api/ordersApi";
import { 
  createShipment, 
  fetchFulfillmentData,
} from "../api/shippingApi";
import { deletePayment } from "../api/paymentsApi";
import { generateInvoicePdf } from "../utils/generateInvoicePdf";
// import { 
//   OrderSummary,
//   FulfillmentAccordion,
//   ShipmentHistory,
//   CustomerDetails,
//   OrderNotes,
//   OrderItemsAccordion,
//   PaymentAccordion 
// } from "../components";
import { 
  OrderDetails, 
  Shipment, 
  OrderItem, 
  PaymentModalState 
} from "../types/types";
import PaymentModal from "../components/PaymentModal";
import { CustomerDetails } from "../components/CustomerDetails";
import { FulfillmentAccordion } from "../components/FulfillmentAccordion";
import { OrderItemsAccordion } from "../components/OrderItemsAccordion";
import { OrderNotes } from "../components/OrderNotes";
import { OrderSummary } from "../components/OrderSummary";
import { PaymentAccordion } from "../components/PaymentAccordion";
import { ShipmentHistory } from "../components/ShipmentHistory";

const safeNumber = (value: any): number => (isNaN(Number(value))) ? 0 : Number(value);

const OrderDetailsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentModalState, setPaymentModalState] = useState<PaymentModalState>({ 
    isOpen: false 
  });
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [fulfillmentStatus, setFulfillmentStatus] = useState("unfulfilled");
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: number }>({});
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  useEffect(() => {
    if (!id || isNaN(Number(id))) {
      navigate("/orders");
      return;
    }
  
    const loadAllData = async () => {
      try {
        setLoading(true);
        const numericId = Number(id);
        
        const [orderResponse, paymentResponse, fulfillmentResponse] = await Promise.all([
          fetchOrderDetails(numericId),
          fetchPayments(numericId),
          fetchFulfillmentData(numericId)
        ]);

        const items = orderResponse.items || orderResponse.line_items || [];
        
        setOrderDetails({
          id: numericId,
          status: orderResponse.status || "Pending",
          total: safeNumber(orderResponse.total),
          outstanding_balance: safeNumber(orderResponse.outstanding_balance),
          customer_name: orderResponse.customer_name || "N/A",
          customer_email: orderResponse.customer_email || "N/A",
          customer_dispensary_name: orderResponse.customer_dispensary_name || "N/A",
          shipping_address: orderResponse.shipping_address || "N/A",
          note: orderResponse.note,
          items,
          fulfillment_status: orderResponse.fulfillment_status
        });
        
        setPayments(paymentResponse || []);
        setOrderItems(fulfillmentResponse.items || []);
        setShipments(fulfillmentResponse.shipments || []);
        setFulfillmentStatus(fulfillmentResponse.fulfillmentStatus || "unfulfilled");

        const initialSelected = (fulfillmentResponse.items || []).reduce((acc: any, item: any) => {
          const key = `${item.product_id}-${item.variant_id || 'null'}`;
          acc[key] = 0;
          return acc;
        }, {});
        setSelectedItems(initialSelected);

      } catch (error) {
        console.error("Error loading data:", error);
        setSnackbar({ open: true, message: "Failed to load order data" });
      } finally {
        setLoading(false);
      }
    };
  
    loadAllData();
  }, [id, navigate]);

  const handlePaymentSuccess = async () => {
    if (!id) return;
    try {
      const numericId = Number(id);
      const [details, paymentHistory] = await Promise.all([
        fetchOrderDetails(numericId),
        fetchPayments(numericId)
      ]);
      setOrderDetails(details);
      setPayments(paymentHistory || []);
    } catch (error) {
      console.error("Error refreshing order details:", error);
      setSnackbar({ open: true, message: "Failed to refresh order data" });
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    if (window.confirm("Are you sure you want to delete this payment?")) {
      try {
        await deletePayment(paymentId);
        await handlePaymentSuccess();
        setSnackbar({ open: true, message: "Payment deleted successfully" });
      } catch (error) {
        console.error("Error deleting payment:", error);
        setSnackbar({ open: true, message: "Failed to delete payment" });
      }
    }
  };

  const handleItemQuantityChange = (
    productId: number, 
    variantId: number | null, 
    quantity: number
  ) => {
    const key = `${productId}-${variantId || 'null'}`;
    const item = orderItems.find(item => 
      item.product_id === productId && 
      (item.variant_id === variantId || (item.variant_id === null && variantId === null))
    );
    
    if (!item) return;

    setSelectedItems(prev => ({
      ...prev,
      [key]: Math.max(0, Math.min(quantity, item.remaining_quantity))
    }));
  };

  const handleCreateShipment = async () => {
    if (!id || !trackingNumber) {
      setSnackbar({ open: true, message: "Please enter tracking number" });
      return;
    }

    const items = Object.entries(selectedItems)
      .filter(([_, qty]) => qty > 0)
      .map(([key, qty]) => {
        const [productId, variantId] = key.split('-');
        return {
          product_id: parseInt(productId),
          variant_id: variantId === 'null' ? null : parseInt(variantId),
          quantity: qty
        };
      });

    if (items.length === 0) {
      setSnackbar({ open: true, message: "Please select at least one item to ship" });
      return;
    }

    setIsSubmitting(true);
    try {
      await createShipment(Number(id), {
        tracking_number: trackingNumber,
        carrier,
        notes,
        items
      });

      setSnackbar({ open: true, message: "Shipment created successfully" });
      
      const [fulfillmentData, updatedDetails] = await Promise.all([
        fetchFulfillmentData(Number(id)),
        fetchOrderDetails(Number(id))
      ]);
      
      setOrderDetails(updatedDetails);
      setOrderItems(fulfillmentData.items || []);
      setShipments(fulfillmentData.shipments || []);
      setFulfillmentStatus(fulfillmentData.fulfillmentStatus || "unfulfilled");

      setTrackingNumber("");
      setCarrier("");
      setNotes("");
      setSelectedItems({});
    } catch (err) {
      console.error("Error creating shipment:", err);
      setSnackbar({ open: true, message: "Failed to create shipment" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGeneratePDF = () => {
    if (orderDetails) {
      generateInvoicePdf(orderDetails, payments, id || "");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!orderDetails) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Error: Order details not found.</Typography>
      </Box>
    );
  }

  return (
    <Box p={isMobile ? 2 : 4}>
      <OrderSummary
        orderDetails={orderDetails}
        fulfillmentStatus={fulfillmentStatus}
        id={id || ""}
        isMobile={isMobile}
        generatePDF={handleGeneratePDF}
      />

      <CustomerDetails orderDetails={orderDetails} />

      {orderDetails.note && <OrderNotes note={orderDetails.note} />}

      <OrderItemsAccordion items={orderDetails.items || orderDetails.line_items || []} />

      <PaymentAccordion
        payments={payments}
        onEditPayment={(payment) => setPaymentModalState({
          isOpen: true,
          paymentId: payment.id,
          initialData: {
            amount: payment.amount,
            method: payment.method,
            status: payment.status,
          }
        })}
        onDeletePayment={handleDeletePayment}
        onAddPayment={() => setPaymentModalState({ isOpen: true })}
      />
        <FulfillmentAccordion
        orderItems={orderItems}
        selectedItems={selectedItems}
        trackingNumber={trackingNumber}
        carrier={carrier}
        notes={notes}
        isSubmitting={isSubmitting}
        onQuantityChange={handleItemQuantityChange}
        onTrackingChange={(e) => setTrackingNumber(e.target.value)}
        onCarrierChange={(e) => setCarrier(e.target.value)}
        onNotesChange={(e) => setNotes(e.target.value)}
        onSubmit={handleCreateShipment}
      />

      <ShipmentHistory shipments={shipments} />

      {paymentModalState.isOpen && (
        <PaymentModal
          orderId={Number(id)}
          paymentId={paymentModalState.paymentId}
          initialData={paymentModalState.initialData}
          onClose={() => setPaymentModalState({ isOpen: false })}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default OrderDetailsPage;